import * as React from "react";
import { $, merge } from "glamor";
import classNames from "classnames";
import { Attribute } from "./attribute";
import { themeOptions } from "styles/theme";
import type { IBadgeCategories } from "types/insights/product-badges";
import TopColorBadge from "components/insights/badges/top-color-badge";
import me from "models/me";
import _ from "lodash";

interface IProps {
    record: any;
    title: string;
    app: any;
    viewDefSection?: any;
    attributes?: React.ReactNode[];
    dataTest?: string;
    productBadges?: IBadgeCategories;
}

export class Attributes extends React.PureComponent<IProps, any> {
    private styles = merge(
        $(" .attributes", {
            display: "flex",
            overflowWrap: "anywhere",
        }),
        $(" .attributeList", {
            display: "table",
            width: "100%",
        }),
        $(" .attributes.twoColumns .attributeList", {
            flexGrow: 0,
            flexShrink: 0,
            width: "50%",
        }),
        $(" .attributes.twoColumns .attributeList:first-child", {
            paddingRight: "12px",
        }),
        $(" .attributes.twoColumns .attributeList:last-child", {
            paddingLeft: "12px",
        }),
        $(" .attribute", {
            display: "table-row",
        }),
        $(" .attribute .label, .attribute .value", {
            borderTop: `1px solid ${themeOptions.palette.lightGrey}`,
            display: "table-cell",
            fontSize: "14px",
            letterSpacing: "0.2px",
            lineHeight: "20px",
            padding: "10px 12px",
            width: "60%",
            verticalAlign: "top",
        }),
        $(" .attribute .label", {
            backgroundColor: themeOptions.palette.lightestGrey,
            color: themeOptions.palette.darkGrey,
            fontWeight: "bold",
            width: "40%",
        }),
        $(" .attribute .value", {
            color: themeOptions.palette.darkerGrey,
        }),
        $(" .attribute .value .color", {
            display: "inline-block",
            width: "40px",
            height: "20px",
            borderRadius: "3px",
            marginRight: "10px",
            float: "left",
        }),
        $(" .attribute .text", {
            display: "inline-block",
        }),
        $(" .attribute .value .image img", {
            maxHeight: "32px",
        }),
        $(" .attribute .value .typeWrapper", {
            marginLeft: "20px",
            display: "flex",
            flexDirection: "column",
        }),
        $(" .attribute .value.complex", {
            color: themeOptions.palette.darkGrey,
        }),
        $(" .attribute .value.complex .main", {
            color: themeOptions.palette.darkerGrey,
            marginBottom: "6px",
        }),
        $(" .attributeList .attribute:last-child .label, .attributeList .attribute:last-child .value", {
            borderBottom: `1px solid ${themeOptions.palette.lightGrey}`,
        }),
        $(" .badgeContainer", {
            display: "block",
        }),
        $(" .badgeValue", {
            marginBottom: "5px",
        }),
    );

    private canShowAttribute(element) {
        const schema = element.type === "field" && _.find(element._map?._root?.entries, (item) => item[0] === "schema");
        const fieldElement = schema && schema[1];
        if (fieldElement && me.account_type === "buyer") {
            return fieldElement.display_to_buyers;
        }
        if (fieldElement && me.account_type === "brand") {
            return fieldElement.display;
        }
        return true;
    }

    render() {
        const { title, record, viewDefSection, attributes = [], dataTest, productBadges } = this.props;
        const hasTwoColumns = false; // app.viewport === VIEWPORT_HD || app.viewport === VIEWPORT_DESKTOP;
        const attributesToRender = attributes || [];

        if (viewDefSection) {
            viewDefSection.elements.map((element, i) => {
                const value = element.getValue(record);
                if (value && element.reference !== "description" && this.canShowAttribute(element)) {
                    // we show description elsewhere
                    if (element.label.toLowerCase() === "color" && value && productBadges?.color) {
                        const colorContent = (
                            <div className="badgeContainer">
                                <div className="badgeValue">{value}</div>{" "}
                                <TopColorBadge productBadges={productBadges} />
                            </div>
                        );
                        attributesToRender.push(<Attribute key={i} label={element.label} value={colorContent} />);
                    } else {
                        attributesToRender.push(<Attribute key={i} label={element.label} value={value} />);
                    }
                }
            });
        }

        if (attributesToRender.length === 0) {
            return null;
        }

        const column1 = [];
        const column2 = [];

        attributesToRender.forEach((attributeJSX, i) => {
            if (!hasTwoColumns || i % 2 === 0) {
                column1.push(attributeJSX);
            } else {
                column2.push(attributeJSX);
            }
        });

        return (
            <div className="panel" {...this.styles} data-test={dataTest}>
                <h3>{title}</h3>
                <div className={classNames("attributes", { twoColumns: hasTwoColumns })}>
                    <div className="attributeList">{column1}</div>
                    {hasTwoColumns && <div className="attributeList">{column2}</div>}
                </div>
            </div>
        );
    }
}
